import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  CreditCard,
  Receipt,
  Cancel,
  OpenInNew,
  Link,
  Refresh,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import {
  getSubscription,
  createBillingPortalSession,
  previewCancellation,
  cancelSubscription,
  reactivateSubscription,
  formatAmount,
  formatDate,
  getPaymentHistory,
  updateSubscriptionPlan,
  getPaymentMethod,
} from "../services/paymentService";
import SubscriptionPlanSelector from "./SubscriptionPlanSelector";
import BillingAddressEditor from "./BillingAddressEditor";
import BillingEmailEditor from "./BillingEmailEditor";
import PaymentMethodEditor from "./PaymentMethodEditor";
import BillingDashboardSkeleton from "./BillingDashboardSkeleton";

// Helper function to calculate pricing display based on new model
const calculatePricingDisplay = (roomCount) => {
  if (roomCount <= 3) {
    return {
      description: `Up to 3 rooms: $9/month`,
      total: 9.00
    };
  } else {
    const additionalRooms = roomCount - 3;
    const additionalCost = additionalRooms * 3;
    return {
      description: `3 rooms ($9) + ${additionalRooms} additional room${additionalRooms !== 1 ? 's' : ''} ($${additionalCost})`,
      total: 9.00 + additionalCost
    };
  }
};

export default function BillingDashboard({ organizationId, onSubscriptionChange }) {
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [recentPayments, setRecentPayments] = useState([]);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [cancellationPreview, setCancellationPreview] = useState(null);
  const [loadingCancelPreview, setLoadingCancelPreview] = useState(false);
  const [planSelectorOpen, setPlanSelectorOpen] = useState(false);
  const [billingEmail, setBillingEmail] = useState('');
  const [paymentMethod, setPaymentMethod] = useState(null);

  const { enqueueSnackbar } = useSnackbar();

  const loadSubscriptionData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🔄 Loading subscription data for organization:', organizationId);
      
      // Load subscription details
      const subscriptionData = await getSubscription(organizationId);
      console.log('📊 Subscription data received:', subscriptionData);
      setSubscription(subscriptionData.subscription);
      
      // Set billing email from subscription data
      if (subscriptionData.subscription?.billingEmail) {
        setBillingEmail(subscriptionData.subscription.billingEmail);
      }
      
      // Load payment method details
      try {
        const paymentMethodData = await getPaymentMethod(organizationId);
        setPaymentMethod(paymentMethodData.paymentMethod);
      } catch (pmError) {
        console.error("Error loading payment method:", pmError);
        setPaymentMethod(null);
      }
      
      // Load payment history (directly from Stripe)
      try {
        const paymentData = await getPaymentHistory(organizationId);
        setRecentPayments(paymentData.payments || []);
        console.log(`Loaded ${paymentData.payments?.length || 0} payments`);
      } catch (paymentError) {
        console.error("Error loading payment history:", paymentError);
        setRecentPayments([]);
      }
      
    } catch (error) {
      console.error("Error loading subscription:", error);
      enqueueSnackbar("Failed to load subscription details", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [organizationId, enqueueSnackbar]);

  useEffect(() => {
    loadSubscriptionData();
  }, [loadSubscriptionData]);

  // Refresh when page becomes visible (user returns to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('🔄 Page visible - refreshing billing data...');
        loadSubscriptionData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadSubscriptionData]);

  const handleRefresh = async () => {
    console.log('🔄 Manual refresh triggered');
    await loadSubscriptionData();
    enqueueSnackbar("Billing data refreshed", { variant: "success" });
  };



  const handleBillingPortal = async () => {
    try {
      console.log("Opening billing portal for organization:", organizationId);
      const response = await createBillingPortalSession(organizationId);
      console.log("Billing portal response:", response);
      
      if (response.url) {
        window.open(response.url, "_blank");
      } else {
        throw new Error("No URL received from billing portal");
      }
    } catch (error) {
      console.error("Error opening billing portal:", error);
      const errorMessage = error.response?.data?.details || error.response?.data?.error || error.message || "Failed to open billing portal";
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };

  const handleOpenCancelDialog = async () => {
    try {
      setLoadingCancelPreview(true);
      setCancelDialogOpen(true);
      const preview = await previewCancellation(organizationId);
      setCancellationPreview(preview);
    } catch (error) {
      console.error("Error loading cancellation preview:", error);
      enqueueSnackbar("Failed to load cancellation details", { variant: "error" });
      setCancellationPreview({ hasProrations: false, prorationAmount: 0, message: "No pending charges." });
    } finally {
      setLoadingCancelPreview(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setCanceling(true);
      const result = await cancelSubscription(organizationId, false); // Cancel at period end
      
      let message = "Subscription will be canceled at the end of the current period";
      if (result.prorationCharge && result.prorationCharge.success) {
        message += ` (${formatAmount(result.prorationCharge.amount)} proration charge was processed)`;
      }
      
      enqueueSnackbar(message, { variant: "success" });
      setCancelDialogOpen(false);
      setCancellationPreview(null);
      loadSubscriptionData(); // Refresh data
      onSubscriptionChange?.();
    } catch (error) {
      console.error("Error canceling subscription:", error);
      const errorMessage = error.response?.data?.error || "Failed to cancel subscription";
      const errorDetails = error.response?.data?.details;
      enqueueSnackbar(`${errorMessage}${errorDetails ? `: ${errorDetails}` : ''}`, { variant: "error" });
    } finally {
      setCanceling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      await reactivateSubscription(organizationId);
      enqueueSnackbar("Subscription has been reactivated successfully!", {
        variant: "success",
      });
      loadSubscriptionData(); // Refresh data
      onSubscriptionChange?.();
    } catch (error) {
      console.error("Error reactivating subscription:", error);
      const errorMessage = error.response?.data?.error || "Failed to reactivate subscription";
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };

  const handleViewReceipt = (receiptUrl) => {
    if (receiptUrl) {
      window.open(receiptUrl, '_blank');
    } else {
      enqueueSnackbar("Receipt not available", { variant: "warning" });
    }
  };

  const handlePlanUpdate = async (maxRooms) => {
    try {
      const result = await updateSubscriptionPlan(organizationId, maxRooms);
      await loadSubscriptionData(); // Refresh data
      onSubscriptionChange?.(); // Notify parent component
      return result; // Return result for SubscriptionPlanSelector to use
    } catch (error) {
      console.error('Error updating plan:', error);
      throw error; // Re-throw to let PlanSelector handle the error display
    }
  };

  const getStatusColor = (status, cancelAtPeriodEnd) => {
    if (status === "active" && cancelAtPeriodEnd) {
      return "warning";
    }
    switch (status) {
      case "active":
        return "success";
      case "trialing":
        return "info";
      case "past_due":
        return "warning";
      case "canceled":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusText = (status, cancelAtPeriodEnd, cancelAt) => {
    if (status === "active" && cancelAtPeriodEnd) {
      return cancelAt ? `Cancels ${formatDate(cancelAt)}` : "Cancels at Period End";
    }
    switch (status) {
      case "active":
        return "Active";
      case "trialing":
        return "Trial";
      case "past_due":
        return "Past Due";
      case "canceled":
        return "Canceled";
      case "incomplete":
        return "Incomplete";
      default:
        return status;
    }
  };

  if (loading) {
    return <BillingDashboardSkeleton />;
  }

  if (!subscription) {
    return (
      <Alert severity="error">
        No subscription found. Please contact support if you believe this is an error.
      </Alert>
    );
  }

  return (
    <>
      <Grid container spacing={3}>
        {/* Subscription Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Subscription Status</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <IconButton onClick={handleRefresh} size="small" title="Refresh billing data">
                    <Refresh />
                  </IconButton>
                  <Chip
                    label={getStatusText(subscription.status, subscription.cancelAtPeriodEnd, subscription.cancelAt)}
                    color={getStatusColor(subscription.status, subscription.cancelAtPeriodEnd)}
                    size="small"
                  />
                </Box>
              </Box>

              <Typography variant="h4" color="primary" gutterBottom>
                {formatAmount(subscription.monthlyAmount)}
                <Typography variant="body2" component="span" color="text.secondary">
                  /month
                </Typography>
              </Typography>

              <Typography variant="body2" color="text.secondary" gutterBottom>
                {calculatePricingDisplay(subscription.maxRooms).description}
              </Typography>

              {subscription.cancelAtPeriodEnd && (
                <Alert severity="warning" sx={{ my: 2 }}>
                  <Typography variant="body2">
                    Your subscription will cancel on {formatDate(subscription.cancelAt || subscription.currentPeriodEnd)}.
                    You can reactivate it at any time before then.
                  </Typography>
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Current Plan
                </Typography>
                <Typography variant="body1">
                  {subscription.maxRooms} Room{subscription.maxRooms !== 1 ? 's' : ''} 
                  {subscription.roomsCount !== undefined && (
                    <span style={{ color: 'text.secondary', fontSize: '0.9em' }}>
                      {' '}({subscription.roomsCount} used)
                    </span>
                  )}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Current Period Ends
                </Typography>
                <Typography variant="body1">
                  {formatDate(subscription.currentPeriodEnd)}
                </Typography>
              </Box>

              {subscription.isActive && (
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button
                    variant="contained"
                    startIcon={<CreditCard />}
                    onClick={handleBillingPortal}
                    size="small"
                  >
                    Manage Billing
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setPlanSelectorOpen(true)}
                    size="small"
                  >
                    Change Plan
                  </Button>
                  {subscription.cancelAtPeriodEnd ? (
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleReactivateSubscription}
                      size="small"
                    >
                      Reactivate
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Cancel />}
                      onClick={handleOpenCancelDialog}
                      size="small"
                    >
                      Cancel
                    </Button>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Payments */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Recent Payments</Typography>
                <Button
                  variant="text"
                  endIcon={<OpenInNew />}
                  onClick={handleBillingPortal}
                  size="small"
                >
                  View All
                </Button>
              </Box>

              {recentPayments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No payments yet
                </Typography>
              ) : (
                <List dense>
                  {recentPayments.map((payment) => (
                    <ListItem 
                      key={payment.id} 
                      disableGutters
                      sx={{
                        cursor: payment.receiptUrl ? 'pointer' : 'default',
                        '&:hover': {
                          backgroundColor: payment.receiptUrl ? 'action.hover' : 'transparent',
                        },
                        borderRadius: 1,
                        mb: 0.5
                      }}
                      onClick={() => payment.receiptUrl && handleViewReceipt(payment.receiptUrl)}
                    >
                      <Receipt sx={{ mr: 2 }} color="action" />
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body1">
                              {formatAmount(payment.amount)}
                            </Typography>
                            {payment.receiptUrl && (
                              <Tooltip title="View receipt">
                                <Link fontSize="small" color="primary" />
                              </Tooltip>
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {payment.description || `${payment.roomsCount} room${payment.roomsCount !== 1 ? "s" : ""}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(payment.date)}
                            </Typography>
                          </Box>
                        }
                      />
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={payment.status}
                          color={payment.status === "succeeded" ? "success" : "error"}
                          size="small"
                        />
                        {payment.receiptUrl && (
                          <Tooltip title="Open receipt">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewReceipt(payment.receiptUrl);
                              }}
                            >
                              <OpenInNew fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Billing Information */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Billing Information
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Billing Model
                  </Typography>
                  <Typography variant="body1">
                    Tiered subscription ($9 for up to 3 rooms, $3/additional room)
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Room Limit
                  </Typography>
                  <Typography variant="body1">
                    {subscription.maxRooms} room{subscription.maxRooms !== 1 ? 's' : ''} maximum
                  </Typography>
                </Grid>
              </Grid>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Your subscription plan allows up to {subscription.maxRooms} room{subscription.maxRooms !== 1 ? 's' : ''}. 
                  You can upgrade your plan anytime to get more rooms. Currently using {subscription.roomsCount || 0} of {subscription.maxRooms} rooms.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Billing Address */}
        <Grid item xs={12}>
          <BillingAddressEditor organizationId={organizationId} />
        </Grid>

        {/* Billing Email */}
        <Grid item xs={12} md={6}>
          <BillingEmailEditor 
            organizationId={organizationId} 
            currentEmail={billingEmail}
            onEmailUpdated={(email) => setBillingEmail(email)}
          />
        </Grid>

        {/* Payment Method */}
        <Grid item xs={12} md={6}>
          <PaymentMethodEditor 
            organizationId={organizationId} 
            currentPaymentMethod={paymentMethod}
            onPaymentMethodUpdated={(pm) => setPaymentMethod(pm)}
          />
        </Grid>
      </Grid>

      {/* Cancel Subscription Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          {loadingCancelPreview ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={3}>
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <Typography>Checking for pending charges...</Typography>
            </Box>
          ) : cancellationPreview ? (
            <Box>
              <Typography gutterBottom>
                Are you sure you want to cancel your subscription? Your subscription will remain active
                until the end of your current billing period ({formatDate(subscription.currentPeriodEnd)}).
              </Typography>
              
              {cancellationPreview.hasProrations ? (
                <Alert severity="warning" sx={{ my: 2 }}>
                  <Typography variant="body1" gutterBottom>
                    <strong>Pending Charges Found</strong>
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    {cancellationPreview.message}
                  </Typography>
                  
                  {cancellationPreview.prorationDetails && cancellationPreview.prorationDetails.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Charge Details:
                      </Typography>
                      {cancellationPreview.prorationDetails.map((detail, index) => (
                        <Box key={index} sx={{ mt: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="body2">
                            {detail.description}
                          </Typography>
                          <Typography variant="body2" color="error.main" fontWeight="bold">
                            {formatAmount(detail.amount)}
                          </Typography>
                        </Box>
                      ))}
                      
                      <Box sx={{ mt: 2, p: 1, bgcolor: 'error.light', borderRadius: 1 }}>
                        <Typography variant="body1" color="error.dark" fontWeight="bold">
                          Total to be charged: {formatAmount(cancellationPreview.prorationAmount)}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  
                  <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
                    These charges will be processed immediately before cancellation to settle any room additions made during your current billing period.
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="info" sx={{ my: 2 }}>
                  <Typography variant="body2">
                    {cancellationPreview.message}
                  </Typography>
                </Alert>
              )}
              
              <Typography sx={{ mt: 2 }} color="text.secondary">
                You can reactivate your subscription at any time before the end of your billing period.
              </Typography>
            </Box>
          ) : (
            <Typography color="error">
              Unable to load cancellation details. Please try again.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCancelDialogOpen(false);
            setCancellationPreview(null);
          }}>
            Keep Subscription
          </Button>
          <Button
            onClick={handleCancelSubscription}
            color="error"
            disabled={canceling || loadingCancelPreview || !cancellationPreview}
            variant="contained"
          >
            {canceling ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Processing...
              </>
            ) : cancellationPreview?.hasProrations ? (
              `Charge ${formatAmount(cancellationPreview.prorationAmount)} & Cancel`
            ) : (
              "Cancel Subscription"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Plan Selector Dialog */}
      <SubscriptionPlanSelector
        open={planSelectorOpen}
        onClose={() => setPlanSelectorOpen(false)}
        currentSubscription={subscription}
        organizationId={organizationId}
        onPlanUpdate={handlePlanUpdate}
      />
    </>
  );
} 